// Core heat simulation. Pure, dt-based, framework-free. The marshmallow's
// internal heat is integrated from the player's distance to the flame; while
// heat sits in the golden band it browns (accrues toastedness); above the burn
// threshold it chars.

import { HEAT_MIN, HEAT_MAX, TOAST_TARGET } from './constants';

export interface HeatState {
  /** Internal heat, 0..100. */
  heat: number;
  /** Browning progress, 0..100. */
  toastedness: number;
  /** Once true, stays true — the marshmallow is charred. */
  burnt: boolean;
  /** Whether heat is currently inside the golden band. */
  inBand: boolean;
}

export interface HeatParams {
  /** Heat-in rate (per second) at full proximity (distanceNorm = 0). */
  flameIntensity: number;
  /** Ambient cooling rate (per second). */
  cooling: number;
  /** Golden band lower bound (heat units). */
  bandMin: number;
  /** Golden band upper bound (heat units). */
  bandMax: number;
  /** Toastedness gained per second while in band. */
  toastRate: number;
  /** Heat above this burns the marshmallow. */
  burnThreshold: number;
}

const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

/** Fresh marshmallow: cold, untoasted, not burnt. */
export function freshHeatState(): HeatState {
  return { heat: HEAT_MIN, toastedness: 0, burnt: false, inBand: false };
}

/** Heat gained per second at a given normalized distance (0 = at flame). */
export function heatInRate(distanceNorm: number, flameIntensity: number): number {
  const d = clamp(distanceNorm, 0, 1);
  return flameIntensity * (1 - d);
}

/** How centered heat is within the band: 1 at the exact center, 0 at the edges
 *  or anywhere outside the band. Used to grade toast quality. */
export function bandCenteredness(heat: number, bandMin: number, bandMax: number): number {
  if (heat < bandMin || heat > bandMax) return 0;
  const center = (bandMin + bandMax) / 2;
  const halfWidth = (bandMax - bandMin) / 2;
  if (halfWidth <= 0) return heat === center ? 1 : 0;
  return 1 - Math.abs(heat - center) / halfWidth;
}

/**
 * Advance the heat state by `dt` seconds. Returns a NEW state (input is not
 * mutated). `windHeat` is an additive per-second perturbation (heat units/sec)
 * supplied by the wind generator; pass 0 for none.
 */
export function stepHeat(
  state: HeatState,
  distanceNorm: number,
  dt: number,
  params: HeatParams,
  windHeat = 0,
): HeatState {
  const dHeat = (heatInRate(distanceNorm, params.flameIntensity) - params.cooling + windHeat) * dt;
  const heat = clamp(state.heat + dHeat, HEAT_MIN, HEAT_MAX);

  const inBand = heat >= params.bandMin && heat <= params.bandMax;
  const toastedness = inBand
    ? clamp(state.toastedness + params.toastRate * dt, 0, TOAST_TARGET)
    : state.toastedness;
  const burnt = state.burnt || heat > params.burnThreshold;

  return { heat, toastedness, burnt, inBand };
}
